import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import {
  getSocket,
  holdSocket,
  releaseSocket,
  staffRealtimeAllowed,
  type WsNotification,
  type WsDeviceStatus,
  type WsTicketUpdate,
  type WsSubscriberChange,
  type WsBillingEvent,
  type WsJobProgress,
  type WsDashboardUpdate,
  type WsOnuStatusChange,
  type WsOnuAlarm,
  type WsOnuDiscovered,
  type WsDeviceRecovery,
  type WsOnuSignalUpdate,
  type WsTechnicianLocation,
  type WsTechnicianStatus,
  type WsJobUpdate,
  type WsMisrouteAlert,
} from '@/lib/socket';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';

// ── Core socket hook ────────────────────────────────────────────────────

/**
 * The staff live feed (/ws). `enabled` is false for a session that gets none — a partner's own login,
 * or a Login-as-Partner tab (see staffRealtimeAllowed) — and then nothing connects, `on`/`emit` do
 * nothing, and the UI shows no connection state at all rather than a "Reconnecting…" that never ends.
 */
export function useSocket() {
  const partnerId = useAuthStore((s) => s.user?.partnerId ?? null);
  const token = useAuthStore((s) => s.token);
  const enabled = useMemo(() => staffRealtimeAllowed(partnerId, token), [partnerId, token]);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const listenersRef = useRef(new Map<string, ((...args: unknown[]) => void)[]>());

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      return;
    }

    const socket = holdSocket();

    const onConnect = () => {
      setIsConnected(true);
    };

    const onDisconnect = () => {
      setIsConnected(false);
    };

    const onConnected = (data: { onlineUsers: number }) => {
      setOnlineUsers(data.onlineUsers);
    };

    const onUserOnline = (data: { onlineUsers: number }) => {
      setOnlineUsers(data.onlineUsers);
    };

    const onUserOffline = (data: { onlineUsers: number }) => {
      setOnlineUsers(data.onlineUsers);
    };

    const onError = (data: { message: string }) => {
      console.error('[WS] Error:', data.message);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connected', onConnected);
    socket.on('user:online', onUserOnline);
    socket.on('user:offline', onUserOffline);
    socket.on('error', onError);

    if (socket.connected) {
      setIsConnected(true);
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connected', onConnected);
      socket.off('user:online', onUserOnline);
      socket.off('user:offline', onUserOffline);
      socket.off('error', onError);
      // The connection is shared: it closes only when the last consumer lets go.
      releaseSocket();
    };
  }, [enabled]);

  const on = useCallback(<T = unknown>(event: string, handler: (data: T) => void) => {
    if (!enabled) return () => undefined;
    const socket = getSocket();
    const wrapper = (payload: { data: T; timestamp: string }) => {
      handler(payload.data);
    };
    socket.on(event, wrapper as (...args: unknown[]) => void);

    // Track for cleanup
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, []);
    }
    listenersRef.current.get(event)!.push(wrapper as (...args: unknown[]) => void);

    return () => {
      socket.off(event, wrapper as (...args: unknown[]) => void);
    };
  }, [enabled]);

  const emit = useCallback((event: string, data?: unknown) => {
    if (!enabled) return;
    const socket = getSocket();
    if (socket.connected) {
      socket.emit(event, data);
    }
  }, [enabled]);

  return { enabled, isConnected, onlineUsers, on, emit };
}

// ── Specialized hooks ───────────────────────────────────────────────────

/**
 * Subscribe to real-time notifications (toast + callback)
 */
export function useRealtimeNotifications(
  onNotification?: (n: WsNotification) => void,
) {
  const { on, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsub = on<WsNotification>('notification', (data) => {
      // Show toast
      const toastType = data.type === 'error' ? 'error'
        : data.type === 'warning' ? 'warning'
        : data.type === 'success' ? 'success'
        : 'info';

      toast[toastType]?.(data.title, data.message);

      // Custom callback
      onNotification?.(data);
    });

    return unsub;
  }, [isConnected, on, onNotification]);
}

/**
 * Subscribe to dashboard metric updates
 */
export function useDashboardUpdates(
  onUpdate: (data: WsDashboardUpdate) => void,
) {
  const { on, emit, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;

    // Subscribe to dashboard channel
    emit('subscribe:dashboard');

    const unsub = on<WsDashboardUpdate>('dashboard:update', onUpdate);

    return () => {
      unsub();
      emit('unsubscribe:dashboard');
    };
  }, [isConnected, on, emit, onUpdate]);
}

/**
 * Subscribe to device status changes
 */
export function useDeviceStatus(
  onStatusChange: (data: WsDeviceStatus) => void,
  deviceId?: string,
) {
  const { on, emit, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;

    emit('subscribe:devices', deviceId ? { deviceId } : undefined);

    const unsub = on<WsDeviceStatus>('device:status', onStatusChange);

    return () => {
      unsub();
      emit('unsubscribe:devices', deviceId ? { deviceId } : undefined);
    };
  }, [isConnected, on, emit, onStatusChange, deviceId]);
}

/**
 * Subscribe to ticket updates
 */
export function useTicketUpdates(
  onUpdate: (data: WsTicketUpdate) => void,
) {
  const { on, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    return on<WsTicketUpdate>('ticket:update', onUpdate);
  }, [isConnected, on, onUpdate]);
}

/**
 * Subscribe to subscriber changes
 */
export function useSubscriberChanges(
  onChange: (data: WsSubscriberChange) => void,
) {
  const { on, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    return on<WsSubscriberChange>('subscriber:change', onChange);
  }, [isConnected, on, onChange]);
}

/**
 * Fires when the backend links an ONU to a subscriber from its live PPPoE session.
 *
 * The match runs the moment a session comes online, which is not a moment the operator is doing
 * anything — so the page has to be told rather than wait to be reloaded.
 */
export interface WsOnuLinked {
  subscriberId: string;
  onu: { id: string; serial: string; oltName: string; ponPort: number; onuIndex: number; status: string };
  via: 'acs' | 'acs_suffix' | 'onu_mac' | null;
  matchedOn: string | null;
}

export function useOnuLinked(onLinked: (data: WsOnuLinked) => void) {
  const { on, isConnected } = useSocket();
  useEffect(() => {
    if (!isConnected) return;
    return on<WsOnuLinked>('subscriber:onu-linked', onLinked);
  }, [isConnected, on, onLinked]);
}

/**
 * Subscribe to billing events
 */
export function useBillingEvents(
  onEvent: (data: WsBillingEvent) => void,
) {
  const { on, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    return on<WsBillingEvent>('billing:event', onEvent);
  }, [isConnected, on, onEvent]);
}

/**
 * Subscribe to background job progress
 */
export function useJobProgress(
  onProgress: (data: WsJobProgress) => void,
) {
  const { on, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    return on<WsJobProgress>('job:progress', onProgress);
  }, [isConnected, on, onProgress]);
}

// ── OLT/ONU real-time hooks ─────────────────────────────────────────────

/**
 * Subscribe to ONU status changes (from polling or SNMP traps)
 */
export function useOnuStatusChanges(
  onChange: (data: WsOnuStatusChange) => void,
) {
  const { on, emit, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    emit('subscribe:devices');
    const unsub = on<WsOnuStatusChange>('onu:status-change', onChange);
    return () => {
      unsub();
      emit('unsubscribe:devices');
    };
  }, [isConnected, on, emit, onChange]);
}

/**
 * Subscribe to ONU optical signal alarms
 */
export function useOnuAlarms(
  onAlarm: (data: WsOnuAlarm) => void,
) {
  const { on, emit, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    emit('subscribe:devices');
    const unsub = on<WsOnuAlarm>('onu:alarm', onAlarm);
    return () => {
      unsub();
      emit('unsubscribe:devices');
    };
  }, [isConnected, on, emit, onAlarm]);
}

/**
 * Subscribe to newly discovered ONUs from T4 polling
 */
export function useOnuDiscovery(
  onDiscovered: (data: WsOnuDiscovered) => void,
) {
  const { on, emit, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    emit('subscribe:devices');
    const unsub = on<WsOnuDiscovered>('onu:discovered', onDiscovered);
    return () => {
      unsub();
      emit('unsubscribe:devices');
    };
  }, [isConnected, on, emit, onDiscovered]);
}

/**
 * Subscribe to ONU signal power updates (from T3 polling or on-demand)
 */
export function useOnuSignalUpdates(
  onUpdate: (data: WsOnuSignalUpdate) => void,
) {
  const { on, emit, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    emit('subscribe:devices');
    const unsub = on<WsOnuSignalUpdate>('onu:signal-update', onUpdate);
    return () => {
      unsub();
      emit('unsubscribe:devices');
    };
  }, [isConnected, on, emit, onUpdate]);
}

/**
 * Subscribe to OLT recovery events (after failover)
 */
export function useDeviceRecovery(
  onRecovery: (data: WsDeviceRecovery) => void,
) {
  const { on, emit, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    emit('subscribe:devices');
    const unsub = on<WsDeviceRecovery>('device:recovery', onRecovery);
    return () => {
      unsub();
      emit('unsubscribe:devices');
    };
  }, [isConnected, on, emit, onRecovery]);
}

/** Payload broadcast to the tenant when a customer WhatsApp message arrives. */
export interface WsWhatsAppIncoming {
  conversationId: string;
  phone: string;
  customerName: string | null;
  preview: string;
}

/**
 * Subscribe to inbound WhatsApp messages (broadcast to the whole tenant — no
 * room subscription needed). Used for the global bell + toast alert.
 */
export function useWhatsAppIncoming(
  onIncoming: (data: WsWhatsAppIncoming) => void,
) {
  const { on, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected) return;
    return on<WsWhatsAppIncoming>('whatsapp:incoming', onIncoming);
  }, [isConnected, on, onIncoming]);
}

/**
 * Live field-dispatch feed for the supervisor (technician positions/status +
 * job changes). Joins the `dispatch:<tenant>` room on the server.
 */
export function useDispatchEvents(handlers: {
  onLocation?: (d: WsTechnicianLocation) => void;
  onStatus?: (d: WsTechnicianStatus) => void;
  onJob?: (d: WsJobUpdate) => void;
  onMisroute?: (d: WsMisrouteAlert) => void;
}) {
  const { on, emit, isConnected } = useSocket();
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!isConnected) return;
    emit('subscribe:dispatch');
    const unsubs = [
      on<WsTechnicianLocation>('technician:location-update', (d) => ref.current.onLocation?.(d)),
      on<WsTechnicianStatus>('technician:status-change', (d) => ref.current.onStatus?.(d)),
      on<WsJobUpdate>('job:update', (d) => ref.current.onJob?.(d)),
      on<WsMisrouteAlert>('job:misroute', (d) => ref.current.onMisroute?.(d)),
    ];
    return () => {
      unsubs.forEach((u) => u());
      emit('unsubscribe:dispatch');
    };
  }, [isConnected, on, emit]);
}
